import React from 'react';
import { Card, Typography, Loader, LabelValue, Separator, Spacing, Icon, Button } from '@design-system/index';
import '../../DetailsPage.scss';
import { Grid } from '@design-system/components/Grid/Grid';
import { formatDate } from '@utils/dates';
import { useDetailsProfileVM } from './useDetailsProfileVM';
import { CONTRIBUTION_TYPE, GENDER, FAMILY_STATUS } from '@utils/enumTranslation';
import { ModifierLine } from '@assets/images/icons';
import { useNavigate } from 'react-router-dom';

export default function DetailsProfile() {
    const { isLoading, isError, member } = useDetailsProfileVM();
    const navigate = useNavigate();

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche adhérent
            </Typography>
        );
    }

    if (!member) {
        return <></>;
    }

    return (
        <div className="details-member-container">
            <div className="details-member-container-body">
                <Card
                    headerType="very-light"
                    headerTitle="Informations administratives"
                    headerAction={
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/members-management/members/${member.id}/edit#administrative-section`)}
                            icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}
                        >
                            Modifier
                        </Button>
                    }
                >
                    <Grid columns={4}>
                        <LabelValue label="N°COSEM" value={member.cosemNumber} />
                        <LabelValue label="Matricule" value={member.administrativeInformation.registrationNumber} />
                        <LabelValue label="Ancien matricule" value={member.administrativeInformation.oldRegistrationNumber} />
                        <LabelValue label="Organisme" value={member.administrativeInformation.organizationLabel} />
                        <LabelValue label="Code Etat" value={member.administrativeInformation.stateCodeLabel} />
                        <LabelValue label="Service" value={member.administrativeInformation.serviceCodeLabel} />
                        <LabelValue
                            label="Type de cotisation"
                            value={CONTRIBUTION_TYPE[member.administrativeInformation.contributionType]}
                        />
                        <LabelValue label="Date d'adhésion" value={formatDate(member.administrativeInformation.registeredAt)} />
                        <LabelValue label="Date de sortie" value={member.administrativeInformation.unregisteredAt ? formatDate(member.administrativeInformation.unregisteredAt) : ''} />
                    </Grid>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Informations personnelles"
                    headerAction={
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/members-management/members/${member.id}/edit#personal-section`)}
                            icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}
                        >
                            Modifier
                        </Button>
                    }
                >
                    <Grid columns={3}>
                        <LabelValue label="Nom" value={member.personalInformation.lastname.toUpperCase()} />
                        <LabelValue label="Prénom" value={member.personalInformation.firstname.toUpperCase()} />
                        <LabelValue label="Nom de naissance" value={member.personalInformation.birthName?.toUpperCase()} />
                        <LabelValue label="Date de naissance" value={formatDate(member.personalInformation.birthDate)} />
                        <LabelValue label="Lieu de naissance" value={member.personalInformation.birthPlace} />
                        <LabelValue label="Situation familiale" value={FAMILY_STATUS[member.personalInformation.familyStatus]} />
                    </Grid>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Coordonnées"
                    headerAction={
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/members-management/members/${member.id}/edit#contact-section`)}
                            icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}
                        >
                            Modifier
                        </Button>
                    }
                >
                    <Grid columns={3}>
                        <LabelValue label="N° Téléphone" value={member.contactInformation.phone} />
                        <LabelValue label="Mail" value={member.contactInformation.email} />
                        <LabelValue label="Adresse" value={member.contactInformation.address} />
                        {member.contactInformation.additionalAddress && <LabelValue label="Complément d'adresse" value={member.contactInformation.additionalAddress} />}
                        <LabelValue label="Code postal" value={member.contactInformation.zipCode} />
                        <LabelValue label="Ville" value={member.contactInformation.city} />
                    </Grid>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Commentaires"
                    headerAction={
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/members-management/members/${member.id}/edit#comment-section`)}
                            icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}
                        >
                            Modifier
                        </Button>
                    }
                >
                    <Typography intrinsic="p" variant="big" color="grey1" preLine>
                        {member.observation || 'Aucun commentaire'}
                    </Typography>
                </Card>


                
                    
                    <Card
                        headerType="very-light"
                        headerTitle="Famille"
                        headerAction={
                            <Button variant="secondary" onClick={() => navigate(`/members-management/members/${member.id}/edit#family-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                                Modifier
                            </Button>
                        }
                    >

                        {(!member.family.spouse.firstname && member.children.length == 0 && !member.family.observation) &&
                            <Typography intrinsic="p" variant="big" color="grey1" preLine>
                                Aucune information disponible
                            </Typography>
                        }

                        {member.family.spouse.firstname &&
                            <>
                                <Typography intrinsic="p" variant="p2" color="marine">
                                    Conjoint
                                </Typography>
                                <Spacing size="small-medium" />
                                <Grid columns={3}>
                                    <LabelValue label="Nom" value={member.family.spouse.lastname} />
                                    <LabelValue label="Prénom" value={member.family.spouse.firstname} />
                                    {member.family.spouse.cosemMember && 
                                        <>
                                            {member.family.spouse.cosemNumber && 
                                                <LabelValue label="N°COSEM" value={member.family.spouse.cosemNumber} navigateTo={`/members-management/members/${member.family.spouse.id}/details`}/>
                                            }
                                        </>
                                    }
                                    <LabelValue label="Nom de naissance" value={member.family.spouse.birthName} />
                                    <LabelValue label="Date de naissance" value={formatDate(member.family.spouse.birthDate)} />
                                    <LabelValue label="Genre" value={GENDER[member.family.spouse.gender]} />
                                </Grid>
                            </>
                        }
                        {member.children.length > 0 &&
                            member.children.map((child, index) => (
                                <>
                                    <Separator large />
                                    <Typography intrinsic="p" variant="p2" color="marine">
                                        Enfant n°{index + 1}
                                    </Typography>
                                    <Spacing size="small-medium" />
                                    <Grid columns={3}>
                                        <LabelValue label="Nom" value={child.lastname} />
                                        <LabelValue label="Prénom" value={child.firstname} />
                                        <LabelValue label="Genre" value={GENDER[child.gender]} />
                                        <LabelValue label="Date de naissance" value={formatDate(child.birthDate)} />
                                        <LabelValue label="À charge" value={child.dependent ? 'Oui' : 'Non'} />
                                        <LabelValue label="Décédé" value={child.deceased ? 'Oui' : 'Non'} />
                                    </Grid>
                                </>
                            ))}
                        <Separator large />
                        <Typography intrinsic="p" variant="p2" color="grey1">
                            Commentaires
                        </Typography>
                        <Spacing size="small" />
                        <Typography intrinsic="p" variant="big" color="grey1" preLine>
                            {member.family.observation || 'Aucun commentaire'}
                        </Typography>
                    </Card>
                
            </div>
        </div>
    );
}
